import api from 'lib/trpcClient';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table, { type Column } from 'further-ui/components/Table';
import { Link } from 'react-router-dom';
import { sumBy } from 'lodash';

import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { usePagination } from 'further-ui/hooks';
import { createLpInvestmentViewRoute } from 'adminConstants/routes';
import { Column as LayoutColumn } from 'further-ui/components/Layout';
import { blue } from '@mui/material/colors';

const StyledLink = styled(Link)(() => ({
  color: blue[500],
}));

type InvestmentHistoryProps = {
  vehicleId: string;
  companyId: string;
};

const pageId = 'investment-history';

const InvestmentHistory: React.FC<InvestmentHistoryProps> = ({
  vehicleId,
  companyId,
}) => {
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'dealCompletionDate',
  });

  const [investmentHistory, query] =
    api.asset.getInvestmentHistory.useSuspenseQuery({
      vehicleId,
      assetId: companyId,
      ...pagination.properties,
    });

  const investmentHistoryRows = investmentHistory.map((investment) => ({
    ...investment,
    id: `${investment.holdingClassId}_${investment.investmentId}`,
  }));

  // Calculate totals for summary row
  const totalNumberOfUnits = sumBy(
    investmentHistoryRows,
    (row) => row.totalNumberOfUnits,
  );
  const totalInitialInvestment = sumBy(
    investmentHistoryRows,
    (row) => row.investmentAmountTcy,
  );
  const totalCurrentNumberOfUnits = sumBy(
    investmentHistoryRows,
    (row) => row.currentNumberOfUnits,
  );
  const totalCurrentRealisedValue = sumBy(
    investmentHistoryRows,
    (row) => row.currentRealisedValue,
  );
  const totalCurrentUnrealisedValue = sumBy(
    investmentHistoryRows,
    (row) => row.currentUnrealisedValue,
  );

  // Calculate MOIC from summed values
  const totalCurrentValue =
    totalCurrentRealisedValue + totalCurrentUnrealisedValue;
  const totalMOIC =
    totalInitialInvestment > 0
      ? numberToDisplayString(totalCurrentValue / totalInitialInvestment, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

  const firstCurrency = investmentHistoryRows.length
    ? investmentHistoryRows[0].currency
    : undefined;
  const commonCurrency =
    firstCurrency !== undefined &&
    investmentHistoryRows.every((r) => r.currency === firstCurrency)
      ? firstCurrency
      : undefined;

  const columns: Array<Column<(typeof investmentHistoryRows)[number]>> = [
    {
      label: 'Investment date',
      key: 'date',
      width: '170px',
      render: (row) => (
        <StyledLink
          to={createLpInvestmentViewRoute(row.investmentId)}
          target="_blank"
        >
          {row.date.toReadable()}
        </StyledLink>
      ),
    },
    {
      label: 'Holding class',
      key: 'name',
      width: '170px',
      disableSorting: true,
      footer: commonCurrency ? 'Total' : undefined,
    },
    {
      label: 'Total units',
      key: 'totalNumberOfUnits',
      width: '150px',
      disableSorting: true,
      render: (row) => numberToDisplayString(row.totalNumberOfUnits),
      footer: commonCurrency
        ? numberToDisplayString(totalNumberOfUnits)
        : undefined,
    },
    {
      label: 'Initial investment',
      key: 'investmentAmountTcy',
      width: '180px',
      disableSorting: true,
      render: (row) =>
        numberToCurrencyString(row.investmentAmountTcy, {
          currency: row.currency,
        }),
      footer: commonCurrency
        ? numberToCurrencyString(totalInitialInvestment, {
            currency: commonCurrency,
          })
        : undefined,
    },
    {
      label: 'Current units',
      key: 'currentNumberOfUnits',
      width: '180px',
      disableSorting: true,
      render: (row) => numberToDisplayString(row.currentNumberOfUnits),
      footer: commonCurrency
        ? numberToDisplayString(totalCurrentNumberOfUnits)
        : undefined,
    },
    {
      label: 'Current unrealised value',
      key: 'currentUnrealisedValue',
      width: '220px',
      disableSorting: true,
      render: (row) =>
        numberToCurrencyString(row.currentUnrealisedValue, {
          currency: row.currency,
        }),
      footer: commonCurrency
        ? numberToCurrencyString(totalCurrentUnrealisedValue, {
            currency: commonCurrency,
          })
        : undefined,
    },
    {
      label: 'Current realised value',
      key: 'currentRealisedValue',
      width: '220px',
      disableSorting: true,
      render: (row) =>
        numberToCurrencyString(row.currentRealisedValue, {
          currency: row.currency,
        }),
      footer: commonCurrency
        ? numberToCurrencyString(totalCurrentRealisedValue, {
            currency: commonCurrency,
          })
        : undefined,
    },
    {
      label: 'Current MOIC',
      key: 'currentMOIC',
      width: '150px',
      disableSorting: true,
      render: (row) => row.currentMOIC,
      footer: commonCurrency ? totalMOIC : undefined,
    },
    {
      label: 'Last revaluation',
      key: 'latestRevaluationDate',
      width: '180px',
      disableSorting: true,
      render: (row) => row.latestRevaluationDate?.toReadable(),
    },
  ];

  return (
    <LayoutColumn spacing="sm">
      <Typography variant="h5">Investment history</Typography>
      <Table
        scrollable
        emptyMessage="No investment history"
        columns={columns}
        isLoading={query.isFetching}
        rows={investmentHistoryRows}
        rowsTotalCount={investmentHistoryRows.length}
        pagination={pagination}
      />
    </LayoutColumn>
  );
};

export default InvestmentHistory;
